import type { CloseOverviewMember } from "@common/schemas/clientsCloseOverview.schema";
import type { MessageSentBy } from "@common/types/chat";
import type { User } from "@common/types/user";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import {
    DEMO_ENTITIES,
    DEMO_PEOPLE,
} from "@components/dev/boardPlayground/fixtures/demoTasks";

export interface DemoEntity {
    id: number;
    /** Null when unknown; runs then use a demo uuid. */
    uuid: string | null;
    name: string;
    iconColor: string;
    logo: string | null;
}

/** A client workspace the fakes attach to, with the entities they name. */
export interface DemoClient {
    workspaceId: string;
    name: string;
    entities: readonly DemoEntity[];
}

/** A teammate the fakes assign, staff closes with, or show as a viewer. */
export interface DemoPerson {
    id: number;
    firstName: string;
    lastName: string;
    name: string;
    email: string | null;
    avatar: string | null;
    iconColor: string;
    organizationId: string | null;
}

export interface DemoNamedRef {
    id: string;
    name: string;
}

type ListedEntity = Omit<DemoEntity, "uuid" | "name"> & {
    uuid?: string;
    name: string | null;
    isPersonal: boolean;
    isMockEntity: boolean;
};

interface ListedWorkspace {
    id: string;
    name: string;
    entities: readonly ListedEntity[];
}

export const toDemoPerson = ({
    user,
    organizationId,
}: {
    user: User;
    organizationId: string | null;
}): DemoPerson => ({
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    name: user.preferredName ?? user.name,
    email: user.email,
    avatar: user.avatar,
    iconColor: user.iconColor,
    organizationId,
});

/** Generic teammates for an organization or workspace with no members; negative ids never match a real user. */
export const FALLBACK_PEOPLE: readonly DemoPerson[] = DEMO_PEOPLE.map(
    (person, index) => {
        const [firstName, lastName = ""] = person.name.split(" ");
        return {
            id: -(index + 1),
            firstName,
            lastName,
            name: person.name,
            email: person.email,
            avatar: person.avatar,
            iconColor: person.iconColor,
            organizationId: null,
        };
    },
);

export const FALLBACK_ENTITIES: readonly DemoEntity[] = DEMO_ENTITIES.map(
    (entity, index) => ({
        id: -(index + 1),
        uuid: null,
        name: entity.name,
        iconColor: entity.iconColor,
        logo: entity.logo,
    }),
);

/** The entities the fakes may name: named, real business entities, never personal or mock ones. */
export const toDemoEntities = (
    entities: readonly ListedEntity[],
): DemoEntity[] =>
    entities.flatMap((entity): DemoEntity[] =>
        entity.name === null || entity.isMockEntity || entity.isPersonal
            ? []
            : [
                  {
                      id: entity.id,
                      uuid: entity.uuid ?? null,
                      name: entity.name,
                      iconColor: entity.iconColor,
                      logo: entity.logo,
                  },
              ],
    );

/** The organization's clients from its workspace list, business entities only, sorted by id so seeding is stable. */
export const toDemoClientsFromWorkspaces = (
    workspaces: readonly ListedWorkspace[],
): DemoClient[] =>
    workspaces
        .map((workspace) => ({
            workspaceId: workspace.id,
            name: workspace.name,
            entities: toDemoEntities(workspace.entities),
        }))
        .filter((client) => client.entities.length > 0)
        .toSorted((a, b) => a.workspaceId.localeCompare(b.workspaceId));

export const toCloseOverviewMember = (
    person: DemoPerson,
): CloseOverviewMember => ({
    userId: person.id,
    role: UserWorkspaceRole.ACCOUNTANT,
    email: person.email ?? "",
    iconColor: person.iconColor,
    avatar: person.avatar,
    preferredName: person.name,
    organizationId: person.organizationId,
});

export const toMessageSentBy = (person: DemoPerson): MessageSentBy => ({
    id: person.id,
    firstName: person.firstName,
    lastName: person.lastName,
    iconColor: person.iconColor,
    avatar: person.avatar,
    name: person.name,
    email: person.email,
});
